import {
	getImproveAPI,
	type NotesControllerSearchNotesParams,
	type SearchNotesResponseDto,
	type CreateNoteBodyDto,
	type CreateNoteResponseDto,
	type UpdateNoteBodyDto,
	type NoteDto
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";

/**
 * Query key constants for notes cache invalidation
 */
export const NOTES_QUERY_KEYS = {
	NOTES: "notes",
	SEARCH_NOTES: "searchNotes"
} as const;

export type {NoteDto};

/** Search params - lessonEnrollmentId preferred for lesson page context */
export type SearchNotesParams = NotesControllerSearchNotesParams;

/**
 * Get user's notes with optional search filtering
 *
 * For lesson page: pass lessonEnrollmentId (from lesson.enrollmentId).
 * Otherwise: pass courseSlug, contentType, moduleSlug as fallback.
 *
 * Uses a 5-minute stale time since notes don't change frequently.
 * Cache is explicitly invalidated on note updates/deletes.
 */
export const useSearchNotes = (params?: SearchNotesParams) => {
	const axiosConfig = useImproveAxios();
	const {notesControllerSearchNotes} = getImproveAPI();

	return useQuery<SearchNotesResponseDto>({
		queryKey: [NOTES_QUERY_KEYS.SEARCH_NOTES, params],
		queryFn: async () => {
			const response = await notesControllerSearchNotes(params, axiosConfig);
			return response.data;
		},
		staleTime: 5 * 60 * 1000
	});
};

/**
 * Create a note for a lesson within a course.
 * Uses lessonEnrollmentId (from lesson.enrollmentId on the lesson page).
 */
export type CreateNoteParams = CreateNoteBodyDto;
export type CreateNoteResponse = CreateNoteResponseDto;

export const useCreateNote = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {notesControllerCreateNote} = getImproveAPI();

	return useMutation<CreateNoteResponseDto, Error, CreateNoteBodyDto>({
		mutationFn: async (body) => {
			const response = await notesControllerCreateNote(body, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [NOTES_QUERY_KEYS.SEARCH_NOTES]});
		}
	});
};

/**
 * Update a note's content and/or video timestamp
 * 
 * On success, invalidates the notes cache to refetch with updated data.
 * 
 * @example
 * ```typescript
 * const { mutate: updateNote, isPending } = useUpdateNote();
 * 
 * updateNote({
 *   noteId: 123,
 *   content: 'Updated note content',
 *   videoTimestamp: 45.5
 * });
 * ```
 */
export const useUpdateNote = () => {
	const axiosConfig = useImproveAxios();
	const {notesControllerUpdateNote} = getImproveAPI();

	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({noteId, ...body}: UpdateNoteBodyDto & {noteId: number}) => {
			const response = await notesControllerUpdateNote(noteId, body, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate all notes queries to refetch with updated data
			queryClient.invalidateQueries({queryKey: [NOTES_QUERY_KEYS.SEARCH_NOTES]});
		}
	});
};

/**
 * Delete a note
 * 
 * On success, invalidates the notes cache to refetch without the deleted note.
 * 
 * @example
 * ```typescript
 * const { mutate: deleteNote, isPending } = useDeleteNote();
 * 
 * deleteNote(123); // Delete note with ID 123
 * ```
 */
export const useDeleteNote = () => {
	const axiosConfig = useImproveAxios();
	const {notesControllerDeleteNote} = getImproveAPI();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (noteId: number) => {
			const response = await notesControllerDeleteNote(noteId, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate all notes queries to refetch without deleted note
			queryClient.invalidateQueries({queryKey: [NOTES_QUERY_KEYS.SEARCH_NOTES]});
		}
	});
};
